import React, { useEffect, useMemo, useState } from 'react';
import C from '../pages/customer/colors';
import weddingStroll from '../assets/selection-preview/wedding-stroll.jpg';
import portraitFloral from '../assets/selection-preview/portrait-floral.jpg';
import coupleKiss from '../assets/selection-preview/couple-kiss.jpg';
import coupleField from '../assets/selection-preview/couple-field.jpg';
import coupleSunset from '../assets/selection-preview/couple-sunset.jpg';
import portraitEditorial from '../assets/selection-preview/portrait-editorial.jpg';
import { getPictureThumbnailUrl } from '../pictureUrls';
import { formatMoney, multiplyMoney } from '../money';

const SAMPLE_PICTURES = [
  { id: 'preview-1', fileName: 'PICSECT_0001.JPG', publicUrlSmall: weddingStroll },
  { id: 'preview-2', fileName: 'PICSECT_0002.JPG', publicUrlSmall: portraitFloral },
  { id: 'preview-3', fileName: 'PICSECT_0003.JPG', publicUrlSmall: coupleKiss },
  { id: 'preview-4', fileName: 'PICSECT_0004.JPG', publicUrlSmall: coupleField },
  { id: 'preview-5', fileName: 'PICSECT_0005.JPG', publicUrlSmall: coupleSunset },
  { id: 'preview-6', fileName: 'PICSECT_0006.JPG', publicUrlSmall: portraitEditorial },
];

const FILTERS = [
  { value: 'all', label: '全部' },
  { value: 'selected', label: '已选' },
  { value: 'unselected', label: '未选' },
];

function PreviewPhotoTile({ picture, selected, showImageName, onToggle }) {
  return (
    <button
      type="button"
      data-testid={`customer-photo-${picture.id}`}
      data-selected={selected ? 'true' : 'false'}
      onClick={() => onToggle(picture.id, !selected)}
      style={{
        position: 'relative',
        aspectRatio: '4/5',
        padding: 0,
        border: 'none',
        borderRadius: 4,
        overflow: 'hidden',
        cursor: 'pointer',
        background: C.paper2,
        boxShadow: selected ? `inset 0 0 0 3px ${C.brand}` : 'none',
      }}
    >
      <img
        src={getPictureThumbnailUrl(picture)}
        alt=""
        decoding="async"
        style={{
          position: 'absolute', inset: 0,
          width: '100%', height: '100%',
          objectFit: 'cover', objectPosition: 'center',
        }}
      />
      <span style={{
        position: 'absolute', bottom: 7, right: 7,
        width: 23, height: 23, borderRadius: 12,
        background: selected ? C.brand : 'rgba(0,0,0,.35)',
        border: `2px solid ${selected ? C.brand : 'rgba(255,255,255,.7)'}`,
        display: 'grid', placeItems: 'center',
        boxSizing: 'border-box',
        boxShadow: selected ? '0 4px 10px rgba(247,119,176,.45)' : 'none',
      }}>
        {selected && (
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="m5 13 4 4L19 7"/>
          </svg>
        )}
      </span>
      {showImageName && (
        <span style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: '13px 5px 4px',
          fontFamily: C.mono, fontSize: 7,
          color: '#fff', textAlign: 'left',
          background: 'linear-gradient(transparent, rgba(0,0,0,.56))',
          pointerEvents: 'none',
        }}>
          {picture.fileName}
        </span>
      )}
    </button>
  );
}

export default function SelectionLivePreview({
  showImageName,
  forceLimit,
  isOnlinePayment,
  price,
  photoCount,
  hasPackage = true,
}) {
  const limit = Math.max(0, Number(photoCount) || 0);
  const unitPrice = Math.max(0, Number(price) || 0);
  const [selectedIds, setSelectedIds] = useState(['preview-1', 'preview-2']);
  const [filter, setFilter] = useState('all');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (forceLimit && limit > 0) {
      setSelectedIds(ids => ids.slice(0, limit));
    }
  }, [forceLimit, limit]);

  const filteredPictures = useMemo(() => SAMPLE_PICTURES.filter(picture => {
    if (filter === 'selected') return selectedIds.includes(picture.id);
    if (filter === 'unselected') return !selectedIds.includes(picture.id);
    return true;
  }), [filter, selectedIds]);

  const hasLimit = limit > 0;
  const overLimit = hasLimit ? Math.max(0, selectedIds.length - limit) : 0;
  const extraCost = multiplyMoney(overLimit, unitPrice);

  const togglePicture = (pictureId, selected) => {
    if (selected && forceLimit && limit > 0 && selectedIds.length >= limit) {
      setNotice(`最多选择 ${limit} 张照片`);
      return;
    }

    setNotice('');
    setSelectedIds(ids => (
      selected
        ? [...new Set([...ids, pictureId])]
        : ids.filter(id => id !== pictureId)
    ));
  };

  const getFilterCount = value => {
    if (value === 'selected') return selectedIds.length;
    if (value === 'unselected') return SAMPLE_PICTURES.length - selectedIds.length;
    return SAMPLE_PICTURES.length;
  };

  return (
    <div
      data-testid="order-selection-live-preview"
      style={{
        position: 'relative',
        height: 660,
        overflow: 'hidden',
        borderRadius: 24,
        border: `1px solid ${C.hair2}`,
        background: C.paper,
        boxShadow: '0 24px 60px -32px rgba(26,22,20,.38)',
        fontFamily: C.sans,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{
          padding: '27px 15px 12px',
          background: 'rgba(237,230,213,.94)',
          borderBottom: `1px solid ${C.hair}`,
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontFamily: C.mono, fontSize: 8, letterSpacing: 1.2,
                color: C.brand, fontWeight: 600, textTransform: 'uppercase',
              }}>
                PICSECT · 选片
              </div>
              <div style={{
                marginTop: 4, fontFamily: C.serif, fontSize: 22,
                lineHeight: 1.05, color: C.ink,
              }}>
                林屿婚礼选片
              </div>
            </div>
            <div data-testid="preview-selection-counter" style={{ flexShrink: 0, textAlign: 'right' }}>
              <div style={{
                fontFamily: C.serif, fontSize: 27, lineHeight: 1,
                color: C.ink, fontVariantNumeric: 'tabular-nums',
              }}>
                {selectedIds.length}
                <span style={{ color: C.ink3, fontSize: 15 }}>/{limit || '—'}</span>
              </div>
              <div style={{
                marginTop: 3, fontFamily: C.mono, fontSize: 8,
                color: C.ink3, letterSpacing: 1.2,
              }}>
                SELECTED
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={{ height: 4, background: C.hair, borderRadius: 2, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${limit ? Math.min(100, (selectedIds.length / limit) * 100) : 0}%`,
                background: overLimit > 0 ? C.brand : C.ink,
                transition: 'width .25s',
              }} />
            </div>
            <div style={{
              marginTop: 6, display: 'flex', justifyContent: 'space-between',
              gap: 8, fontFamily: C.mono, fontSize: 8, color: C.ink3,
            }}>
              <span>
                {hasLimit
                  ? `${hasPackage ? '套餐内' : '可选'} ${Math.min(selectedIds.length, limit)}/${limit}`
                  : '请先设置可选照片数量'}
              </span>
              {overLimit > 0
                ? <span style={{ color: C.brand2 }}>加片 {overLimit} × ¥{formatMoney(unitPrice)} = ¥{formatMoney(extraCost)}</span>
                : hasLimit && <span>· {hasPackage ? '在套餐内' : '在可选数量内'} ·</span>}
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 20,
          padding: '7px 14px 6px',
          borderBottom: `1px solid ${C.hair}`,
          background: C.paper, flexShrink: 0,
        }}>
          {FILTERS.map(item => (
            <button
              key={item.value}
              type="button"
              data-testid={`preview-filter-${item.value}`}
              aria-pressed={filter === item.value}
              onClick={() => setFilter(item.value)}
              style={{
                padding: '5px 0 6px', border: 'none',
                borderBottom: `2px solid ${filter === item.value ? C.ink : 'transparent'}`,
                background: 'transparent',
                color: filter === item.value ? C.ink : C.ink3,
                fontFamily: C.sans, fontSize: 11,
                fontWeight: filter === item.value ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {item.label} <span style={{ fontSize: 9, opacity: .7 }}>{getFilterCount(item.value)}</span>
            </button>
          ))}
        </div>

        {notice && (
          <div data-testid="preview-limit-message" style={{
            margin: '8px 9px 0', padding: '7px 9px', borderRadius: 7,
            background: '#FBE0E0', color: '#8C2020', fontSize: 10,
            textAlign: 'center', flexShrink: 0,
          }}>
            {notice}
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto', padding: 4 }}>
          {filteredPictures.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 4,
            }}>
              {filteredPictures.map(picture => (
                <PreviewPhotoTile
                  key={picture.id}
                  picture={picture}
                  selected={selectedIds.includes(picture.id)}
                  showImageName={showImageName}
                  onToggle={togglePicture}
                />
              ))}
            </div>
          ) : (
            <div style={{ padding: '46px 0', textAlign: 'center', color: C.ink3, fontSize: 12 }}>
              这里暂时没有照片
            </div>
          )}
        </div>

        <div style={{
          minHeight: 65, padding: '8px 13px 11px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(237,230,213,.96)',
          borderTop: `1px solid ${C.hair}`,
          flexShrink: 0,
        }}>
          <button
            type="button"
            data-testid="preview-review-selection"
            disabled={selectedIds.length === 0}
            style={{
              width: '100%', height: 43, padding: '0 12px',
              borderRadius: 22, border: 'none',
              display: 'flex', alignItems: 'center', gap: 8,
              background: selectedIds.length === 0
                ? 'rgba(26,22,20,.42)'
                : (overLimit > 0 ? C.brand : C.ink),
              color: '#fff', fontFamily: C.sans, fontSize: 12, fontWeight: 600,
              boxShadow: selectedIds.length === 0 ? 'none' : '0 10px 22px -16px rgba(26,22,20,.55)',
            }}
          >
            <span style={{
              width: 29, height: 29, borderRadius: 15,
              background: selectedIds.length === 0
                ? 'rgba(247,119,176,.25)'
                : (overLimit > 0 ? '#fff' : C.brand),
              color: overLimit > 0 ? C.brand2 : '#fff',
              display: 'grid', placeItems: 'center',
              fontVariantNumeric: 'tabular-nums', flexShrink: 0,
            }}>
              {selectedIds.length}
            </span>
            <span style={{ flex: 1, textAlign: 'center' }}>
              {selectedIds.length === 0
                ? '请先选择照片'
                : (overLimit > 0 && isOnlinePayment ? '确认并支付' : '确认选片')}
            </span>
            {overLimit > 0 && isOnlinePayment && <span>¥{formatMoney(extraCost)}</span>}
            <span style={{ opacity: .65 }}>→</span>
          </button>
        </div>
      </div>
    </div>
  );
}
